import {
  ReferralSchema,
  ResourceBookingSchema,
} from '../data/types';

export type FundingPipelineItem = {
  id: string
  referral?: ReferralSchema
  application?: ResourceBookingSchema
  isLegacyApplication: boolean
};

type BuildFundingPipelineItemsArgs = {
  applications: ResourceBookingSchema[]
  resourceId?: string
  partnerSlug?: string
  includeLegacyApplications?: boolean
};

/** Project a canonical funding booking into the partner-referral facade. */
export const referralFromBooking = (booking: ResourceBookingSchema): ReferralSchema | undefined => {
  const envelope = booking.partner_referral;
  if (!envelope) return undefined;
  return {
    ...envelope,
    id: booking.id,
    uid: booking.uid || envelope.uid,
    community_id: booking.community_id || envelope.community_id,
    resource_id: booking.resource_id || envelope.resource_id,
    user_email: booking.user_email || envelope.payload?.email,
  };
};

/** Build the Partner referrals list from community funding bookings. */
export const buildFundingPipelineItems = ({
  applications,
  resourceId,
  partnerSlug,
  includeLegacyApplications = true,
}: BuildFundingPipelineItemsArgs): FundingPipelineItem[] => {
  return applications.flatMap((application) => {
    if (resourceId && application.resource_id && application.resource_id !== resourceId) {
      return [];
    }
    const referral = referralFromBooking(application);
    if (partnerSlug && referral?.partner_slug && referral.partner_slug !== partnerSlug) {
      return [];
    }
    if (partnerSlug && !referral?.partner_slug && application.resource_id !== resourceId) {
      return [];
    }
    const isLegacyApplication = !referral;
    if (isLegacyApplication && !includeLegacyApplications) {
      return [];
    }
    return [{
      id: application.id || referral?.id || '',
      referral,
      application,
      isLegacyApplication,
    }];
  }).filter((item) => Boolean(item.id));
};

/** Search text for pipeline rows; never includes inbound payload PII. */
export const fundingPipelineSearchText = (item: FundingPipelineItem): string => {
  const referral = item.referral;
  const application = item.application;
  return [
    referral?.external_referral_id,
    referral?.referral_code,
    referral?.partner_slug,
    referral?.status,
    referral?.direction,
    application?.user_full_name,
    application?.username,
    application?.resource_name,
    application?.business_name,
  ].filter(Boolean).join(' ').toLowerCase();
};
